import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useState, type CSSProperties, type KeyboardEvent, type RefObject } from "react"
import { Link, useNavigate } from "react-router-dom"
import { usePopover } from "../ds/usePopover"
import type { MatterFile, Proceeding } from "../contract"
import { FEATURE_LABEL, type Feature } from "./copy"
import { optionsFor, shelfOf } from "./proceedings"
import { ProceedingRadios } from "./ProceedingChoice"
import { useProceedingChoice } from "./useProceedingChoice"
import "../case/case.css"
import "./StartHere.css"

/** The five kinds of session and what each is for, in the order they are offered. */
export const SESSION_KINDS: { feature: Feature; line: string }[] = [
  { feature: "research", line: "Questions on the law and your facts" },
  { feature: "notice", line: "What the notice alleges and where it's weak" },
  { feature: "appeal", line: "Should you appeal this order" },
  { feature: "oio", line: "Draft the adjudication order (OIO)" },
  { feature: "oia", line: "The appeal order" },
]

const GAP = 8
const MARGIN = 16
const SHEET_MAX = 480

/**
 * Where a popover sits: under its button, its right (or left) edge on the button's, 8px away; above the
 * button when there is no room below; inside the viewport with a 16px margin. On a phone it is a bottom
 * sheet. Worked out from the button's rect; again on resize and scroll.
 */
function useAnchor(open: boolean, button: RefObject<HTMLElement>, panel: RefObject<HTMLElement>, align: "left" | "right", dep: unknown) {
  const [style, setStyle] = useState<CSSProperties>({ visibility: "hidden" })
  const [sheet, setSheet] = useState(false)
  const place = useCallback(() => {
    const b = button.current?.getBoundingClientRect()
    if (!b) return
    const vw = window.innerWidth
    const vh = window.innerHeight
    if (vw <= SHEET_MAX) { setSheet(true); setStyle({ left: 0, right: 0, bottom: 0 }); return }
    setSheet(false)
    const width = Math.min(320, vw - MARGIN * 2)
    const left = Math.max(MARGIN, Math.min(align === "right" ? b.right - width : b.left, vw - MARGIN - width))
    const height = panel.current?.offsetHeight ?? 0
    const below = vh - b.bottom - GAP - MARGIN
    const above = b.top - GAP - MARGIN
    const up = height > below && above > below
    setStyle(up
      ? { left, width, bottom: vh - b.top + GAP, maxHeight: above }
      : { left, width, top: b.bottom + GAP, maxHeight: Math.max(below, 160) })
  }, [align, button, panel])
  useLayoutEffect(() => { if (open) place() }, [open, place, dep])
  useEffect(() => {
    if (!open) return
    window.addEventListener("resize", place)
    window.addEventListener("scroll", place, true)
    return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true) }
  }, [open, place])
  return { style, sheet }
}

/**
 * One button that opens the list of session types. Research, notice analysis and appeal start on the
 * home page carrying the workspace (the thread joins it when the first question or file is sent);
 * the two order drafts open the simulator under it.
 */
export function NewSession({ caseId, title, label = "New session", primary = true, quiet = false, align = "left", proceedings, files }: {
  caseId: string; title: string; label?: string; primary?: boolean; align?: "left" | "right"
  /** A quiet text button instead of a bordered one (a section's own "Start a session"). */
  quiet?: boolean
  /** What the workspace holds, when known: the session then starts on the proceeding it can use. */
  proceedings?: Proceeding[]; files?: MatterFile[]
}) {
  const navigate = useNavigate()
  const { open, setOpen, button, panel } = usePopover<HTMLButtonElement, HTMLDivElement>()
  const menuId = useId()
  const id = encodeURIComponent(caseId)
  const shelf = useMemo(() => shelfOf({ files: files ?? [], proceedings }), [files, proceedings])
  // Notice analysis and Appeal or not read one document: with several proceedings the reader picks first.
  const [sheet, setSheet] = useState<"notice" | "appeal" | null>(null)
  const notice = useProceedingChoice(shelf, "notice")
  const appeal = useProceedingChoice(shelf, "appeal")
  const door = (d: "notice" | "appeal", doc?: string) => {
    const q = `door=${d}${doc ? `&workspace=${id}&doc=${encodeURIComponent(doc)}` : ""}`
    navigate(`/ask?${q}`, { state: { matter: caseId } })
  }
  const home = (d?: "notice" | "appeal") => () => {
    if (!d) { navigate("/ask", { state: { matter: caseId } }); return }
    const c = d === "notice" ? notice : appeal
    if (c.options.length > 1) { setSheet(d); return }
    door(d, c.options.length === 1 ? c.anchor : undefined)
  }
  const orderLink = (f: "oio" | "oia") => {
    const options = optionsFor(shelf, f)
    const only = options.length === 1 ? options[0] : null
    if (!only) return `/simulator?case=${id}&order=${f}`
    return `/simulator?case=${id}&order=${f}&proceeding=${encodeURIComponent(only.proceeding.id)}&docs=${only.members.map((m) => encodeURIComponent(m.file.doc_id)).join(",")}`
  }
  const act = (f: Feature): { to?: string; go?: () => void } => {
    if (f === "research") return { go: home() }
    if (f === "notice") return { go: home("notice") }
    if (f === "appeal") return { go: home("appeal") }
    return { to: orderLink(f) }
  }
  const sheeted = sheet === "notice" ? notice : sheet === "appeal" ? appeal : null
  const anchor = useAnchor(open, button, panel, align, sheet)
  // Focus goes to the first item on open (arrow keys move from there), and back to the button on Escape (usePopover).
  useEffect(() => {
    if (open && !sheeted) panel.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()
  }, [open, sheeted, panel])
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const all = Array.from(panel.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])
    const at = all.indexOf(document.activeElement as HTMLElement)
    const to = e.key === "ArrowDown" ? (at + 1) % all.length : e.key === "ArrowUp" ? (at - 1 + all.length) % all.length : e.key === "Home" ? 0 : e.key === "End" ? all.length - 1 : -1
    if (to < 0) return
    e.preventDefault()
    all[to]?.focus()
  }
  return (
    <div className="sh-menu" data-align={align} data-open={open || undefined}>
      <button
        ref={button} type="button" className={quiet ? "sh-quiet" : `ncf-btn${primary ? " primary" : ""}`}
        aria-haspopup="menu" aria-expanded={open} aria-controls={open ? menuId : undefined}
        onClick={() => { setSheet(null); setOpen((v) => !v) }}
      >
        {label}
        <svg className="sh-caret" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 4.5 6 8l3.5-3.5" /></svg>
      </button>
      {open && anchor.sheet ? <div className="sh-scrim" aria-hidden="true" /> : null}
      {open && sheeted ? (
        <div ref={panel} id={menuId} role="dialog" aria-label={`${FEATURE_LABEL[sheet as Feature]}: choose the proceeding`} className={`sh-pop sh-sheet${anchor.sheet ? " bottom" : ""}`} style={anchor.style}>
          <ProceedingRadios choice={sheeted} />
          <div className="sh-sheet-go">
            <button type="button" className="ncf-btn" onClick={() => setSheet(null)}>Back</button>
            <button type="button" className="ncf-btn primary" onClick={() => { setOpen(false); door(sheet as "notice" | "appeal", sheeted.anchor) }}>Continue</button>
          </div>
        </div>
      ) : open ? (
        <div ref={panel} id={menuId} role="menu" aria-label={`Session types for ${title}`} className={`sh-pop${anchor.sheet ? " bottom" : ""}`} style={anchor.style} onKeyDown={onKey}>
          {SESSION_KINDS.map(({ feature, line }) => {
            const a = act(feature)
            const body = (<><span className="sh-pop-t">{FEATURE_LABEL[feature]}</span><span className="sh-pop-d">{line}</span></>)
            return a.to ? (
              <Link key={feature} role="menuitem" className="sh-pop-i" to={a.to} onClick={() => setOpen(false)}>{body}</Link>
            ) : (
              <button key={feature} type="button" role="menuitem" className="sh-pop-i" onClick={() => { const next = a.go; if (feature === "notice" || feature === "appeal") { const c = feature === "notice" ? notice : appeal; if (c.options.length > 1) { next?.(); return } } setOpen(false); next?.() }}>{body}</button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

/** Shown once files are in and no session exists: what sessions are, and the one way to begin. */
export function SessionGuide({ caseId, title, proceedings, files, showAction = true }: { caseId: string; title: string; proceedings?: Proceeding[]; files?: MatterFile[]; showAction?: boolean }) {
  return (
    <section className="sh-guide" aria-labelledby="sh-guide-h">
      <h2 id="sh-guide-h" className="sh-guide-h">{files?.length ? "Ask anything about these files" : "Start your first session"}</h2>
      <p className="sh-guide-p">A session is one piece of work. Start as many as you need; each can use this workspace's files.</p>
      <dl className="sh-kinds">
        {SESSION_KINDS.map(({ feature, line }) => (
          <div key={feature}><dt>{FEATURE_LABEL[feature]}</dt><dd>{line}</dd></div>
        ))}
      </dl>
      {showAction ? <NewSession caseId={caseId} title={title} label="Start a session" proceedings={proceedings} files={files} /> : null}
    </section>
  )
}
